import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Drawer, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listProducerRuns } from '../api';
import { formatErrorMessage, resolveErrorMessage } from '../utils/errorMessage';
import ProducerRunViewer from './ProducerRunViewer';

const { Text } = Typography;

const STATUS_COLORS = {
  queued: 'default',
  running: 'gold',
  succeeded: 'green',
  failed: 'red',
  canceled: 'default',
  cancel_requested: 'orange',
};

const STATUS_TEXT = {
  queued: '排队中',
  running: '运行中',
  succeeded: '成功',
  failed: '失败',
  canceled: '已取消',
  cancel_requested: '取消中',
};

const statusTag = (status) => {
  const s = String(status || '');
  return <Tag color={STATUS_COLORS[s] || 'default'}>{STATUS_TEXT[s] || s || '-'}</Tag>;
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-';
  const raw = String(dateTimeString);
  const normalized = raw.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, '$1$2');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const ProduceRunHistoryDrawer = ({ open, schedule, onClose }) => {
  const scheduleId = String(schedule?.schedule_id || '').trim();
  const producerName = String(schedule?.producer_name || '').trim();

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [triggerFilter, setTriggerFilter] = useState(undefined);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState('');

  const refresh = useCallback(async (pageOverride = null, pageSizeOverride = null) => {
    if (!open || !scheduleId) return;
    setLoading(true);
    try {
      const resp = await listProducerRuns({
        schedule_id: scheduleId,
        producer_name: producerName || undefined,
        trigger_type: triggerFilter || undefined,
        statuses: statusFilter ? [statusFilter] : undefined,
        page: pageOverride ?? page,
        page_size: pageSizeOverride ?? pageSize,
      });
      if (resp?.status === 'success') {
        setItems(Array.isArray(resp.items) ? resp.items : []);
        setTotal(resp.total || 0);
        setPage(resp.page || (pageOverride ?? page));
        setPageSize(resp.page_size || (pageSizeOverride ?? pageSize));
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '获取运行历史失败'));
      }
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [open, page, pageSize, producerName, scheduleId, statusFilter, triggerFilter]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageSize(10);
    setStatusFilter(undefined);
    setTriggerFilter(undefined);
  }, [open, scheduleId]);

  useEffect(() => {
    if (!open) return;
    refresh(1, pageSize);
  }, [open, refresh, scheduleId]);

  const columns = useMemo(() => ([
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v) => statusTag(v),
    },
    {
      title: '运行ID',
      dataIndex: 'run_id',
      key: 'run_id',
      width: 360,
      render: (v) => <Text code copyable={{ text: v }}>{v}</Text>,
    },
    {
      title: '触发方式',
      dataIndex: 'trigger',
      key: 'trigger',
      width: 120,
      render: (t) => <Text>{t?.type || '-'}</Text>,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 200,
      render: (v) => <Text>{formatDateTime(v)}</Text>,
    },
    {
      title: '错误',
      dataIndex: 'error',
      key: 'error',
      width: 260,
      render: (e) => {
        const msg = formatErrorMessage(e?.error_message || e?.error_code);
        return (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
            {msg || '-'}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setActiveRunId(row?.run_id || '');
            setDetailOpen(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ]), []);

  return (
    <>
      <Drawer
        title={`运行历史（${schedule?.task_name || scheduleId}）`}
        open={open}
        onClose={onClose}
        width={1100}
        destroyOnClose
        extra={(
          <Space>
            <Select
              style={{ width: 160 }}
              allowClear
              placeholder="状态"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: '排队中', value: 'queued' },
                { label: '运行中', value: 'running' },
                { label: '成功', value: 'succeeded' },
                { label: '失败', value: 'failed' },
                { label: '已取消', value: 'canceled' },
              ]}
            />
            <Select
              style={{ width: 160 }}
              allowClear
              placeholder="触发方式"
              value={triggerFilter}
              onChange={setTriggerFilter}
              options={[
                { label: '定时', value: 'schedule' },
                { label: '手动', value: 'manual' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refresh(1, pageSize)} loading={loading}>
              刷新
            </Button>
          </Space>
        )}
      >
        <Table
          size="small"
          rowKey="run_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1180 }}
          tableLayout="fixed"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
              refresh(p, ps);
            },
          }}
          locale={{ emptyText: '暂无运行记录' }}
        />
      </Drawer>

      <Drawer
        title="运行详情"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={720}
        destroyOnClose
      >
        <ProducerRunViewer
          runId={activeRunId}
          producerName={producerName || schedule?.producer_name}
          title="运行进度"
        />
      </Drawer>
    </>
  );
};

export default ProduceRunHistoryDrawer;
